import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { friendlyAuthError } from "@/lib/auth-errors";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  head: () => ({
    meta: [
      { title: "Set new password — MissionReady" },
      { name: "description", content: "Choose a new password for your MissionReady account after a password reset request. Secure, encrypted password update." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Set new password — MissionReady" },
      { property: "og:description", content: "Choose a new password for your MissionReady account." },
      { property: "og:url", content: "https://missionready.dev/reset-password" },
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/reset-password" }],
  }),
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  // Supabase can land here in three shapes depending on the link format:
  //  - hash tokens (#access_token=...) which the client auto-exchanges
  //  - PKCE code (?code=...)
  //  - token hash (?token_hash=...&type=recovery)
  // Handle all three, plus explicit error params and a timeout fallback.
  useEffect(() => {
    let cancelled = false;
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const errDesc = params.get("error_description") ?? hash.get("error_description") ?? params.get("error") ?? hash.get("error");
      if (errDesc) {
        if (!cancelled) setLinkError("This reset link is invalid or has expired. Request a new one.");
        return;
      }
      const code = params.get("code");
      const tokenHash = params.get("token_hash");
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (!cancelled && error) setLinkError("This reset link is invalid or has expired. Request a new one.");
        else if (!cancelled) setReady(true);
        return;
      }
      if (tokenHash) {
        const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "recovery" });
        if (!cancelled && error) setLinkError("This reset link is invalid or has expired. Request a new one.");
        else if (!cancelled) setReady(true);
        return;
      }
      const { data } = await supabase.auth.getSession();
      if (!cancelled && data.session) setReady(true);
    };
    void run();

    const timer = window.setTimeout(() => {
      if (!cancelled) {
        setLinkError((prev) => prev ?? "We couldn't verify this reset link. It may have expired — request a new one.");
      }
    }, 8000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      sub.subscription.unsubscribe();
    };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setPwError("Password must be at least 8 characters");
      return toast.error("Password must be at least 8 characters");
    }
    if (password !== confirm) {
      setConfirmError("Passwords don't match");
      return toast.error("Passwords don't match");
    }
    setPwError(null);
    setConfirmError(null);
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) return toast.error(friendlyAuthError(error));
    toast.success("Password updated");
    navigate({ to: "/operations" });
  };

  return (
    <main className="flex min-h-dvh items-center justify-center bg-hero px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-card">
          <h1 className="font-display text-2xl font-semibold">Set a new password</h1>
          <p className="mt-1 text-sm text-muted-foreground">Choose a strong password you haven't used before.</p>
          {!ready && linkError ? (
            <div className="mt-6 space-y-3">
              <p role="alert" className="text-sm font-medium text-destructive">{linkError}</p>
              <Link to="/forgot-password" className="inline-block text-sm text-primary underline underline-offset-2">
                Request a new reset link
              </Link>
            </div>
          ) : !ready ? (
            <div className="mt-6 text-sm text-muted-foreground">Verifying reset link…</div>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" type="password" required minLength={8} value={password} onChange={(e) => { setPassword(e.target.value); setPwError(null); }} className="mt-1" autoComplete="new-password" aria-invalid={!!pwError} aria-describedby={pwError ? "pw-error" : undefined} />
                {pwError && <p id="pw-error" className="mt-1 text-xs font-medium text-destructive">{pwError}</p>}
              </div>
              <div>
                <Label htmlFor="pw2">Confirm password</Label>
                <Input id="pw2" type="password" required minLength={8} value={confirm} onChange={(e) => { setConfirm(e.target.value); setConfirmError(null); }} className="mt-1" autoComplete="new-password" aria-invalid={!!confirmError} aria-describedby={confirmError ? "pw2-error" : undefined} />
                {confirmError && <p id="pw2-error" className="mt-1 text-xs font-medium text-destructive">{confirmError}</p>}
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Updating…" : "Update password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
